/**
 * Customer refund request CTA (D3, D9, D11).
 *
 * OIDC path: shell runner owns pending refund mutate; CTA reacts via outcome store.
 * OTP / signed-in: claim in-flight first, then clear matching pending, then local mutate.
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '../../shared/ui/Button.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import { ApiError } from '../../shared/api/ApiClient.js';
import { SignInScreen } from '../auth/SignInScreen.js';
import { canMountEmbeddedSignInForm } from '../auth/routing/canMountEmbeddedSignInForm.js';
import { isSignedInNavChrome } from '../hub/customerHubPaths.js';
import { parkPostPayAuthReturnTarget } from '../post-pay/parkPostPayAuthReturn.js';
import {
  beginPostPayAuthClaimAndClearPending,
  endPostPayAuthClaimInFlight,
  isPostPayAuthClaimGenerationCurrent,
  isPostPayAuthClaimInFlightFor,
  recordPostPayAuthActionOutcome,
  savePendingPostPayRefund,
} from '../post-pay/postPayPendingAuthAction.js';
import { useHasSucceededPostPayAuthOutcome } from '../post-pay/usePostPayAuthActionOutcome.js';
import { useSubmitCustomerRefundRequest } from '../checkout/checkoutApi.js';

interface RefundRequestCtaProps {
  paymentId: string;
  /** When false, CTA is hidden (e.g. resolve-token reported not eligible). */
  refundEligible?: boolean;
  /**
   * Row-tenant API prefix when chrome is `__platform__` (Account payment QR).
   * Must match `QrPaymentView` / `usePaymentStatus` tenant override.
   */
  apiTenantCode?: string;
}

const REFUND_NOT_ELIGIBLE_REASONS = [
  'PAYMENT_COMPLETED',
  'PAYMENT_CANCELLED',
  'PAYMENT_STILL_OPEN',
  'REFUND_ALREADY_REQUESTED',
  'REFUND_IN_PROGRESS',
  'ALREADY_REFUNDED',
  'REFUND_FAILED',
  'GENERIC',
] as const;

type RefundNotEligibleReason = (typeof REFUND_NOT_ELIGIBLE_REASONS)[number];

function isRefundNotEligibleReason(value: string): value is RefundNotEligibleReason {
  return (REFUND_NOT_ELIGIBLE_REASONS as readonly string[]).includes(value);
}

function readRefundNotEligibleReason(error: ApiError): RefundNotEligibleReason | undefined {
  if (error.code !== 'REFUND_NOT_ELIGIBLE') {
    return undefined;
  }
  if (typeof error.data !== 'object' || error.data === null || !('details' in error.data)) {
    return undefined;
  }
  const details = (error.data as { details?: unknown }).details;
  if (typeof details !== 'object' || details === null || !('reason' in details)) {
    return undefined;
  }
  const reason = (details as { reason?: unknown }).reason;
  if (typeof reason !== 'string' || !isRefundNotEligibleReason(reason)) {
    return undefined;
  }
  return reason;
}

function refundRequestErrorMessage(error: Error, t: TFunction<'customer'>): string {
  if (!(error instanceof ApiError) || error.code === undefined) {
    return t('paymentFailure.refundFailed');
  }

  switch (error.code) {
    case 'REFUND_NOT_ELIGIBLE': {
      const reason = readRefundNotEligibleReason(error) ?? 'GENERIC';
      return t(`paymentFailure.refundNotEligibleReason.${reason}`);
    }
    case 'CUSTOMER_REFUND_RATE_LIMITED':
      return t('paymentFailure.refundRateLimited');
    case 'FORBIDDEN':
      return t('paymentFailure.refundForbidden');
    case 'TRANSACTION_NOT_FOUND':
    case 'NOT_FOUND':
      return t('paymentFailure.refundNotFound');
    default:
      return t('paymentFailure.refundFailed');
  }
}

export function RefundRequestCta({
  paymentId,
  refundEligible = false,
  apiTenantCode,
}: RefundRequestCtaProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const { pathname, search } = useLocation();
  const tenantCode = useTenantCode();
  const { session } = useCustomerSession();
  const signedInChrome = isSignedInNavChrome({ session });
  const submitRefund = useSubmitCustomerRefundRequest();
  const [showSignIn, setShowSignIn] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const refundApiTenant =
    apiTenantCode !== undefined && apiTenantCode.trim().length > 0
      ? apiTenantCode.trim()
      : undefined;

  // G1 — live outcome sync (runner may settle while CTA already mounted).
  const outcomeSucceeded = useHasSucceededPostPayAuthOutcome({
    kind: 'refund',
    paymentId,
  });
  const showSubmitted = submitted || outcomeSucceeded;

  const runRefundSubmit = useCallback((): void => {
    if (outcomeSucceeded || submitted) {
      setSubmitted(true);
      return;
    }
    // Runner/CTA already owns this refund — wait for reactive outcome (no second POST).
    if (isPostPayAuthClaimInFlightFor({ kind: 'refund', paymentId })) {
      return;
    }
    // Claim first; matching pending cleared only if begin succeeds (generation token).
    const generation = beginPostPayAuthClaimAndClearPending({
      kind: 'refund',
      paymentId,
    });
    if (generation === null) {
      return;
    }
    submitRefund.mutate(
      {
        paymentId,
        ...(refundApiTenant !== undefined ? { tenantCode: refundApiTenant } : {}),
      },
      {
        onSuccess: () => {
          // Stale after TTL / newer owner — do not record or clear their claim.
          if (
            !isPostPayAuthClaimGenerationCurrent({
              kind: 'refund',
              paymentId,
              generation,
            })
          ) {
            return;
          }
          recordPostPayAuthActionOutcome({ kind: 'refund', paymentId });
          endPostPayAuthClaimInFlight({ kind: 'refund', paymentId });
          setSubmitted(true);
        },
        onError: (error: unknown) => {
          if (
            !isPostPayAuthClaimGenerationCurrent({
              kind: 'refund',
              paymentId,
              generation,
            })
          ) {
            return;
          }
          endPostPayAuthClaimInFlight({ kind: 'refund', paymentId });
          void error;
        },
      },
    );
  }, [outcomeSucceeded, paymentId, refundApiTenant, submitRefund, submitted]);

  const handleSubmit = useCallback((): void => {
    if (!signedInChrome) {
      parkPostPayAuthReturnTarget(`${pathname}${search}`, tenantCode);
      const pendingTenant =
        refundApiTenant !== undefined ? refundApiTenant : tenantCode;
      if (hasCustomerApiTenant(pendingTenant)) {
        savePendingPostPayRefund({
          paymentId,
          tenantCode: pendingTenant,
          ...(refundApiTenant !== undefined
            ? { apiTenantCode: refundApiTenant }
            : {}),
        });
      }
      setShowSignIn(true);
      return;
    }
    runRefundSubmit();
  }, [
    pathname,
    paymentId,
    refundApiTenant,
    runRefundSubmit,
    search,
    signedInChrome,
    tenantCode,
  ]);

  const handleSignedIn = useCallback((customerIdFromAuth?: number): void => {
    setShowSignIn(false);
    const customerId = customerIdFromAuth ?? session?.customerId;
    if (customerId === undefined) {
      return;
    }
    // OTP: same claim-first path as signed-in submit (helper clears matching pending).
    runRefundSubmit();
  }, [runRefundSubmit, session]);

  if (refundEligible !== true) {
    return null;
  }

  const canMountSignIn = canMountEmbeddedSignInForm({ session });
  let submitLabel = t('paymentFailure.refundSubmit');
  if (submitRefund.isPending) {
    submitLabel = t('paymentFailure.refundSubmitting');
  } else if (!signedInChrome) {
    submitLabel = canMountSignIn
      ? t('paymentFailure.refundSignIn')
      : t('paymentFailure.refundContinueSession');
  }

  if (showSignIn && !signedInChrome) {
    if (!canMountSignIn) {
      setShowSignIn(false);
      return null;
    }
    return (
      <SignInScreen
        heading={t('paymentFailure.refundSignInHeading')}
        helperText={t('paymentFailure.refundSignInHelper')}
        onSignedIn={handleSignedIn}
      />
    );
  }

  const showRefundError = submitRefund.isError && submitRefund.error instanceof Error;
  const refundError =
    showRefundError && submitRefund.error instanceof Error ? submitRefund.error : null;

  return (
    <div className="w-full rounded-md border border-[var(--color-border)] p-4 text-left">
      <h3 className="text-base font-semibold">{t('paymentFailure.refundTitle')}</h3>
      <p className="mt-2 text-sm text-[var(--color-on-surface-muted)]">
        {t('paymentFailure.refundDescription')}
      </p>
      {showSubmitted ? (
        <p className="mt-2 text-sm text-[var(--color-success)]" role="status">
          {t('paymentFailure.refundSubmitted')}
        </p>
      ) : null}
      {refundError !== null ? (
        <p className="mt-2 text-sm text-[var(--color-danger)]" role="alert">
          {refundRequestErrorMessage(refundError, t)}
        </p>
      ) : null}
      {!showSubmitted ? (
        <Button
          intent="primary"
          size="md"
          block
          className="mt-3"
          onClick={handleSubmit}
          disabled={submitRefund.isPending}
        >
          {submitLabel}
        </Button>
      ) : null}
    </div>
  );
}
